import { selectBackgroundTask } from "@/store/background-tasks/background-tasks-selector";
import {
  addBackgroundTask,
  updateBackgroundTask,
} from "@/store/background-tasks/background-tasks-slice";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import {
  BackgroundTask,
  TiledImageSheetGenerationTask,
  isTiledImageSheetGenerationTask,
} from "@/utils/background-tasks";
import { assert } from "@faro-lotv/foundation";
import { GUID } from "@faro-lotv/ielement-types";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { DateTime } from "luxon";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type JSX,
} from "react";

type TiledImageSheetGenerationContext = {
  /** Callback to create and start a tiled image sheet generation task in the store */
  startTaskInStore(
    id: GUID,
    sheetName: string,
    iElementId: GUID,
  ): AbortController;
  /** Callback to update a tiled image sheet generation task in the store */
  updateTaskInStore(id: GUID, progress: number): void;
  /** Callback to complete a tiled image sheet generation task in the store */
  completeTask(id: GUID): void;
  /** Callback to report a tiled image sheet generation task as failed */
  failTask(id: GUID, message: string): void;
  /** Callback to report a tiled image sheet generation task has been canceled */
  cancelTask(id: GUID): void;
  /** Get the abort controller for a specific task */
  getController(id: GUID): AbortController | undefined;
};

/** Context which provides the functionalities for managing tiled image sheet generation tasks */
export const TiledImageSheetGenerationContext = createContext<
  TiledImageSheetGenerationContext | undefined
>(undefined);

/**
 * @returns a context useful to start and update tiled image sheet generation tasks in the store
 */
export function useTiledImageSheetGenerationContext(): TiledImageSheetGenerationContext {
  const context = useContext(TiledImageSheetGenerationContext);
  if (!context) {
    throw new Error("TiledImageSheetGenerationContext is not initialized.");
  }
  return context;
}

/**
 * @returns a provider component for the TiledImageSheetGenerationContext
 */
export function TiledImageSheetGenerationContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const [controllers] = useState<Map<GUID, AbortController>>(new Map());

  const startTaskInStore = useCallback(
    (id: GUID, sheetName: string, iElementId: GUID) => {
      const isoDate = DateTime.now().toISO();

      const controller = new AbortController();
      controllers.set(id, controller);

      // Create background task
      const backgroundTask: TiledImageSheetGenerationTask = {
        id,
        createdAt: isoDate,
        changedAt: isoDate,
        type: "TiledImageSheetGeneration",
        devMessage: "Generating tiled image sheet",
        state: BackgroundTaskState.created,
        progress: 1,
        iElementId,
        shouldPreventWindowClose: true,
        metadata: {
          sheetName,
        },
      };
      dispatch(addBackgroundTask(backgroundTask));
      return controller;
    },
    [controllers, dispatch],
  );

  const updateTaskInStore = useCallback(
    (id: GUID, progress: number) => {
      const task = selectBackgroundTask(id)(store.getState());
      assert(
        task && isTiledImageSheetGenerationTask(task),
        "Missing tiled image sheet generation task",
      );
      // Update the percentage of completion and the changedAt time
      const updatedTask: BackgroundTask = {
        ...task,
        state: BackgroundTaskState.started,
        progress,
        changedAt: DateTime.now().toISO(),
      };
      dispatch(updateBackgroundTask(updatedTask));
    },
    [dispatch, store],
  );

  const completeTask = useCallback(
    (id: GUID) => {
      const task = selectBackgroundTask(id)(store.getState());
      assert(
        task && isTiledImageSheetGenerationTask(task),
        "Missing tiled image sheet generation task",
      );
      // Flag the task as succeeded
      const updatedTask: TiledImageSheetGenerationTask = {
        ...task,
        state: BackgroundTaskState.succeeded,
        changedAt: DateTime.now().toISO(),
      };
      dispatch(updateBackgroundTask(updatedTask));
      controllers.delete(id);
    },
    [controllers, dispatch, store],
  );

  const failTask = useCallback(
    (id: GUID, message: string) => {
      const task = selectBackgroundTask(id)(store.getState());
      assert(
        task && isTiledImageSheetGenerationTask(task),
        "Missing tiled image sheet generation task",
      );
      // Flag the task as failed
      const updatedTask: TiledImageSheetGenerationTask = {
        ...task,
        state: BackgroundTaskState.failed,
        changedAt: DateTime.now().toISO(),
        devMessage: message,
      };
      dispatch(updateBackgroundTask(updatedTask));
      controllers.delete(id);
    },
    [controllers, dispatch, store],
  );

  const cancelTask = useCallback(
    (id: GUID) => {
      const task = selectBackgroundTask(id)(store.getState());
      assert(
        task && isTiledImageSheetGenerationTask(task),
        "Missing tiled image sheet generation task",
      );

      // Flag the task as failed
      const updatedTask: TiledImageSheetGenerationTask = {
        ...task,
        state: BackgroundTaskState.aborted,
        changedAt: DateTime.now().toISO(),
      };
      dispatch(updateBackgroundTask(updatedTask));
      controllers.delete(id);
    },
    [controllers, dispatch, store],
  );

  const getController = useCallback(
    (id: GUID) => controllers.get(id),
    [controllers],
  );

  const contextValue = useMemo(
    () => ({
      startTaskInStore,
      updateTaskInStore,
      completeTask,
      failTask,
      cancelTask,
      getController,
    }),
    [
      cancelTask,
      completeTask,
      failTask,
      startTaskInStore,
      updateTaskInStore,
      getController,
    ],
  );

  return (
    <TiledImageSheetGenerationContext value={contextValue}>
      {children}
    </TiledImageSheetGenerationContext>
  );
}
